import {
  Theme as ChakraTheme,
  ColorProps,
  getToken,
  useColorMode,
  useTheme,
} from "@chakra-ui/react";
import { useMemo } from "react";

export type CardColorScheme =
  | "yellow"
  | "white"
  | "accentInk"
  | "accentGreen"
  | "accentGold"
  | "accentBlue"
  | "accentMagenta"
  // Legacy-look schemes; aliased to the nearest accent here.
  | "titleBrand"
  | "titleBlue"
  | "titleGreen"
  | "titleYellow"
  | "titleTeal";
type NoDataColorScheme = "grey";

export type CardVariant = "panel" | "tile";

type CardPallete = Record<string, ColorProps["color"]>;
type ThemeDefintion = { light: CardPallete; dark: CardPallete };
type Theme = Record<CardColorScheme | NoDataColorScheme, ThemeDefintion>;

// intentsify.io cards: white panels with a hairline border, no title bars.
// Accent schemes add a short coloured bar above the title instead.
const panel = {
  light: {
    bg: "white",
    border: "gray.100",
    title: "gray.900",
    divider: "transparent",
    helpIcon: "gray.400",
  },
  dark: {
    bg: "brand.375",
    border: "#2C3230",
    title: "gray.50",
    divider: "transparent",
    helpIcon: "gray.400",
  },
} satisfies ThemeDefintion;

const withAccent = (light: string, dark = light): ThemeDefintion => ({
  light: { ...panel.light, accent: light },
  dark: { ...panel.dark, accent: dark },
});

/** Background for `tile` cards (KPI tiles), per colour mode. */
export const tileBg = { light: "gray.50", dark: "brand.300" } as const;

const accents = {
  accentInk: withAccent("gray.900", "gray.50"),
  accentGreen: withAccent("teal.600", "teal.500"),
  accentGold: withAccent("yellow.500"),
  accentBlue: withAccent("blue.500"),
  accentMagenta: withAccent("pink.500", "pink.300"),
};

const theme = {
  ...accents,
  titleBrand: accents.accentInk,
  titleBlue: accents.accentBlue,
  titleGreen: accents.accentGreen,
  titleYellow: accents.accentGold,
  titleTeal: accents.accentGreen,
  yellow: {
    light: {
      bg: "yellow.500",
      border: "transparent",
      title: "gray.900",
      divider: "transparent",
      helpIcon: "gray.900",
    },
    dark: {
      bg: "yellow.700",
      border: "transparent",
      title: "white",
      divider: "transparent",
      helpIcon: "white",
    },
  },
  white: panel,
  grey: {
    light: { ...panel.light, bg: "gray.25", title: "gray.700" },
    dark: panel.dark,
  },
} satisfies Theme;

export const usePalette = (
  isEnabled: boolean,
  cardColorScheme: CardColorScheme | NoDataColorScheme
) => {
  const { colorMode } = useColorMode();
  const chakraTheme = useTheme<ChakraTheme>();

  return useMemo(() => {
    const cardPallete: CardPallete = isEnabled
      ? theme[cardColorScheme][colorMode]
      : // Keep the accent so empty cards still read as part of their set.
        {
          ...theme.grey[colorMode],
          accent: (theme[cardColorScheme][colorMode] as CardPallete).accent,
        };

    return getResolvedPallete(cardPallete, chakraTheme);
  }, [isEnabled, cardColorScheme, colorMode, chakraTheme]);
};

const getResolvedPallete = (
  cardPallete: CardPallete,
  chakraTheme: ChakraTheme
) => {
  const resolvedPalleteEntries = Object.entries(cardPallete).map(
    ([key, value]) => [key, getToken("colors", value as string)(chakraTheme)]
  );

  return Object.fromEntries(resolvedPalleteEntries) as Record<string, string>;
};
